import $ from 'jquery';
import { noop, compose, extend, chain } from 'underscore';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FilterService } from '@bingads-webui/filter-service';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { closeImageStyle, InlineEditorStyle, uiblockerStyle } from '@bingads-webui-campaign-react/common-styles';
import { TargetingSettingsContext } from '@bingads-webui-campaign/targeting-settings-scaffolds';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { createAllSettingsGrid } from '@bingads-webui-campaign/all-settings-grid';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { pagePaddingStyle } from '@bingads-webui-campaign-react/page-padding-style';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { ProgressiveLoadingBlock, getFilteredCampaignTypeFromRawFilter } from '@bingads-webui-campaign-react/shell-ui-common';
import { WaterfallFilterStorage, WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { allSettingsSummaryStyle } from './styles/settings-summary-style';
import { getFilterConfig } from './filter-config';
import { getAdditionalPlugins } from './additional-plugins';
import { getToolbarConfig } from './toolbar-config';

const getSettingsSummaryPageStyles = (_, { palette = {} }) => extend({
  topGridPanelCustomStyles: {
    '& .previewTable': {
      wordBreak: 'break-word',
    },
  },
  '@global': Object.assign(
    {},
    pagePaddingStyle(),
    allSettingsSummaryStyle,
    InlineEditorStyle(),
    closeImageStyle({ palette }),
    uiblockerStyle({ palette })
  ),
});

const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);

export class SettingsSummary extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      backboneGridView: null,
    };
    this.willUnMount = false;
    this.columnAction = noop;
    this.viewport = React.createRef();

    this.createGrid();
  }

  componentWillUnmount() {
    if (this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
    this.willUnMount = true;
  }

  getGridName() {
    return 'Settings';
  }

  createGrid() {
    const {
      p$legacyInit,
      scope,
      preferenceService,
      crossPageSelectPlugin,
      toolbarPlugin,
      onGridInjected,
      perfMarker,
      permissions,
      prefetchChunks,
    } = this.props;

    if (this.state && this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
    if (this.state) {
      this.setState({ backboneGridView: null });
    }

    // merge the scopeService instance and scope entity object together
    const scopeAdapter = extend({
      customerId: scope.values.cid,
      accountId: scope.values.aid,
      campaignId: scope.values.campaignId,
    }, scope);

    const AllSettingsGridView = React.lazy(() => { // eslint-disable-line arrow-body-style
      return p$legacyInit.bind(this)
        .then(() => TargetingSettingsContext.fetchPreferenceConfig(this.getGridName()))
        .then((preferenceConfig) => {
          if (this.willUnMount) {
            return {};
          }

          const filterConfig = getFilterConfig({
            entitySchema: TargetingSettingsContext.CampaignSchema,
            i18n: TargetingSettingsContext.i18n,
            sessionStore: TargetingSettingsContext.jsDataSessionStore,
            preferenceStoreFactory: TargetingSettingsContext.preferenceStoreFactory.getFilters,
            legacyGlobalI18n: TargetingSettingsContext.legacyGlobalI18n,
            schemaI18nCampaign: TargetingSettingsContext.s$i18n,
            cacheService: TargetingSettingsContext.cacheService,
            dataContextManager: TargetingSettingsContext.dataContextManager,
            preferenceService,
            appConfig: TargetingSettingsContext.config,
            jsData: TargetingSettingsContext.jsDataCampaign,
          });

          const filterService = new FilterService(filterConfig);
          if (scopeAdapter.campaignId) {
            TargetingSettingsContext.state.set('campaign_id', scopeAdapter.campaignId);
          } else {
            TargetingSettingsContext.state.unset('campaign_id');
          }

          const waterfallFilterStorage = new WaterfallFilterStorage({
            preferenceService,
          });
          const getCampaignTypeGlobalFilter = () => {
            const filters = waterfallFilterStorage.getFilters([WaterfallFilterType.CampaignType]);
            const rawFilter = chain(filters).first().result('filter').value();
            return getFilteredCampaignTypeFromRawFilter(rawFilter);
          };

          const gridView = createAllSettingsGrid({
            filterService,
            gridName: this.getGridName(),
            getAdditionalPlugins: getAdditionalPlugins({
              gridName: this.getGridName(),
              permissions,
              preferenceService,
              toolbarPlugin,
              crossPageSelectPlugin,
              scope,
              i18n: TargetingSettingsContext.i18n,
            }),
            perfMarker,
            preferenceConfig,
            prefetchChunks,
            viewport: $('.all-settings-page'),
            customHeaderOffset: () => $('.sticky-container').height(),
            getCampaignTypeGlobalFilter,
            instrumentation: this.props.instrumentation,
          });

          this.setState({
            backboneGridView: gridView,
          });

          onGridInjected({
            backboneGridView: gridView,
            deselectAll: gridView.grid.gridView.deselectAll.bind(gridView.grid.gridView),
          });

          return {
            default: () => (<StyledGridViewWrapperWithFilterContext gridView={gridView} gridClassName="bingadsgrid small-padding-left" />),
          };
        });
    });

    this.allSettingsGrid = (<AllSettingsGridView />);
  }

  render() {
    const {
      serverContext: {
        CurrentAccount: { TimeZone },
      },
      panelView,
      showTopGridPanel,
      renderToolbarContainer,
    } = this.props;
    const topGridPanel = showTopGridPanel ? (<TopGridPanel
      panelView={panelView}
      customClasses={{
        topGridPanelCustomStyles: this.props.classes.topGridPanelCustomStyles,
      }}
    />) : null;

    const topOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <Fragment >
          {topGridPanel}
        </Fragment>
      </React.Suspense>
    );

    const stickyTopOfGridContent = (
      <Fragment>
        {renderToolbarContainer()}
      </Fragment>
    );

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <StyledTablePageContainer
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.allSettingsGrid}
          backboneGridView={this.state.backboneGridView}
          customClasses={{
            tablePageContainer: 'all-settings-page small-padding-left',
            gridViewContainer: 'all-settings-table',
          }}
          timeZone={TimeZone}
          scrollToTop={this.props.forceHideToolbar && this.props.showTopGridPanel}
          forceResetScrollFlag={this.props.forceResetScrollFlag}
        />
      </React.Suspense>
    );
  }
}

SettingsSummary.propTypes = {
  p$legacyInit: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({}),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    topGridPanelCustomStyles: PropTypes.object.isRequired,
  }).isRequired,
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  panelView: PropTypes.func.isRequired,
  onGridInjected: PropTypes.func.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  forceHideToolbar: PropTypes.bool.isRequired,
  forceResetScrollFlag: PropTypes.bool.isRequired,
  prefetchChunks: PropTypes.func.isRequired,
};

export const ManagedSettingsSummary = compose(
  withInstrumentation,
  withSiteMap,
  WrappedComponent => withDefaultStyles(WrappedComponent, getSettingsSummaryPageStyles),
  withTablePageStateManager({ getToolbarConfig })
)((SettingsSummary));

